import type { MindmapSyntaxNode, MindmapSyntaxTree } from './Ast';

const DEFAULT_DIRECTION: MindmapSyntaxTree['meta']['direction'] = 'right';

interface ParseContext {
  stack: MindmapSyntaxNode[];
  tree: MindmapSyntaxTree;
}

const isDashLine = (line: string) => line.trimStart().startsWith('-');

const sanitiseLine = (line: string) => line.replace(/\t/g, '  ');

const countLeadingSpaces = (line: string) => {
  const match = line.match(/^\s*/);
  return match ? match[0].length : 0;
};

const extractTokens = (content: string) => {
  const markerIndex = content.indexOf('|||');
  if (markerIndex === -1) return { content: content.trimEnd(), tokens: [] as string[] };

  const before = content.slice(0, markerIndex).trimEnd();
  const after = content.slice(markerIndex + 3).trim();
  if (!after) return { content: before, tokens: [] as string[] };

  const tokens = after
    .split(/\s+/)
    .filter((token) => token.startsWith('#'))
    .map((token) => token.trim())
    .filter(Boolean);

  return { content: before, tokens };
};

const extractTitleAndLink = (content: string) => {
  const match = content.match(/^(.*?)(?:\s+->\s*(\S.*))?$/);
  if (!match) return { title: content.trim(), link: undefined };
  const title = match[1]?.trim() ?? '';
  const link = match[2]?.trim();
  return { title, link: link && link.length > 0 ? link : undefined };
};

const createNode = (title: string, link: string | undefined, tokens: string[], depth: number, line: number): MindmapSyntaxNode => ({
  title,
  link,
  tokens,
  children: [],
  depth,
  line,
});

const attachNode = (ctx: ParseContext, node: MindmapSyntaxNode, indentLevel: number) => {
  const { stack, tree } = ctx;

  if (indentLevel <= 0 || stack.length === 0) {
    node.depth = 0;
    if (!tree.root) {
      tree.root = node;
    } else {
      tree.errors.push(`Multiple root nodes detected at line ${node.line}. Keeping the first root.`);
      tree.root.children.push(node);
      node.depth = 1;
      stack.length = 1;
      stack[0] = tree.root;
      stack.push(node);
      return;
    }
    stack.length = 0;
    stack.push(node);
    return;
  }

  const boundedDepth = Math.min(indentLevel, stack.length);
  const parentIndex = Math.max(0, Math.min(boundedDepth - 1, stack.length - 1));
  const parent = stack[parentIndex];

  if (!parent) {
    tree.root ??= node;
    stack.length = 0;
    stack.push(node);
    return;
  }

  const actualDepth = parent.depth + 1;
  node.depth = actualDepth;
  parent.children.push(node);

  stack.length = parentIndex + 1;
  stack.push(node);
};

export function parseMindmapSyntax(input: string): MindmapSyntaxTree {
  const normalised = input.replace(/\r\n?/g, '\n').split('\n').map(sanitiseLine);
  const tree: MindmapSyntaxTree = {
    root: null,
    meta: { direction: DEFAULT_DIRECTION },
    errors: [],
  };
  const ctx: ParseContext = { stack: [], tree };

  normalised.forEach((rawLine, index) => {
    const lineNumber = index + 1;
    const trimmed = rawLine.trim();
    if (!trimmed) return;
    if (trimmed.startsWith('//')) return;
    if (!isDashLine(rawLine)) return;

    const leadingSpaces = countLeadingSpaces(rawLine);
    const indentLevel = Math.floor(leadingSpaces / 2);

    const withoutDash = rawLine.trimStart().slice(1);
    const { content, tokens } = extractTokens(withoutDash);

    const { title, link } = extractTitleAndLink(content.trim());
    const node = createNode(title, link, tokens, indentLevel, lineNumber);

    attachNode(ctx, node, indentLevel);
  });

  if (tree.root) {
    const directionToken = tree.root.tokens.find((token) => /^#dir-(left|right|both)$/i.test(token));
    if (directionToken) {
      const normalisedToken = directionToken.toLowerCase();
      if (normalisedToken === '#dir-left') tree.meta.direction = 'left';
      else if (normalisedToken === '#dir-both') tree.meta.direction = 'both';
      else tree.meta.direction = 'right';
    }
  }

  return tree;
}
